import { useEffect, useRef, useState, type FormEvent } from "react";
import { api, currentTenant, fmtTime, type LogEvent } from "../api";
import { EventRow } from "../components/EventList";
import { useGroups, useLogGroups } from "../hooks";

const MAX_LINES = 2000;

interface Line {
  key: string;
  kind: "log" | "error" | "issue" | "notice";
  event?: LogEvent;
  stream?: string;
  text?: string;
}

export function tailUrl(ticket: string, params: Record<string, string>): string {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  const q = new URLSearchParams({ ticket, ...params });
  const tenant = currentTenant();
  if (tenant) q.set("tenant", tenant);
  return `${proto}//${window.location.host}/v1/tail?${q.toString()}`;
}

export function linesFromMessage(msg: any): Line[] {
  switch (msg.type) {
    case "events":
      return msg.events.map((e: LogEvent) => ({
        key: `${msg.seq}:${e.event_id}`, kind: "log", event: e, stream: `${msg.log_group}/${msg.log_stream}`,
      }));
    case "error":
      return [{
        key: `${msg.seq}:err`, kind: "error",
        text: `${fmtTime(msg.occurrence.ts)}  ${msg.log_group}  ${msg.occurrence.error_type}: ${msg.occurrence.message}  (issue ×${msg.issue.count}, ${msg.issue.status})`,
      }];
    case "issue":
      return [{
        key: `${msg.seq}:issue`, kind: "issue",
        text: `issue “${msg.issue.title}”: ${msg.change?.from ?? "new"} → ${msg.change?.to} by ${msg.change?.actor}`,
      }];
    case "dropped":
      return [{ key: `drop:${Date.now()}`, kind: "notice", text: `${msg.count} messages dropped (client too slow)` }];
    default:
      return [];
  }
}

export function TailPage() {
  const groups = useGroups();
  const logGroups = useLogGroups();
  const [form, setForm] = useState({ group: "", log_group: "", pattern: "", types: "log,error" });
  const [lines, setLines] = useState<Line[]>([]);
  const [status, setStatus] = useState<"idle" | "connecting" | "live" | "closed" | "error">("idle");
  const [error, setError] = useState<string | null>(null);
  const [paused, setPaused] = useState(false);
  const [follow, setFollow] = useState(true);
  const ws = useRef<WebSocket | null>(null);
  const buffer = useRef<Line[]>([]);
  const pausedRef = useRef(false);
  const bottom = useRef<HTMLDivElement | null>(null);

  pausedRef.current = paused;

  useEffect(() => () => ws.current?.close(), []);
  useEffect(() => {
    if (follow && !paused) bottom.current?.scrollIntoView?.({ block: "end" });
  }, [lines, follow, paused]);
  useEffect(() => {
    if (!paused && buffer.current.length) {
      const pending = buffer.current;
      buffer.current = [];
      setLines((prev) => [...prev, ...pending].slice(-MAX_LINES));
    }
  }, [paused]);

  async function start(e: FormEvent) {
    e.preventDefault();
    ws.current?.close();
    setError(null);
    setStatus("connecting");
    try {
      const { ticket } = await api<{ ticket: string }>("/v1/tail/tickets", { method: "POST" });
      const params: Record<string, string> = { group: form.group, types: form.types };
      if (form.log_group) params.log_group = form.log_group;
      if (form.pattern) params.pattern = form.pattern;
      const sock = new WebSocket(tailUrl(ticket, params));
      ws.current = sock;
      sock.onmessage = (ev) => {
        const msg = JSON.parse(ev.data);
        if (msg.type === "subscribed") return setStatus("live");
        if (msg.type === "error" && msg.code) {
          setError(msg.message);
          setStatus("error");
          return;
        }
        const add = linesFromMessage(msg);
        if (!add.length) return;
        if (pausedRef.current) buffer.current = [...buffer.current, ...add].slice(-MAX_LINES);
        else setLines((prev) => [...prev, ...add].slice(-MAX_LINES));
      };
      sock.onclose = () => setStatus((s) => (s === "error" ? s : "closed"));
    } catch (err) {
      setError(String((err as Error).message ?? err));
      setStatus("error");
    }
  }

  function stop() {
    ws.current?.close();
    ws.current = null;
  }

  const lgOptions = logGroups.data?.filter((l) => l.group_name === form.group) ?? [];
  return (
    <section aria-labelledby="tail-title">
      <h1 id="tail-title">Live tail</h1>
      <form className="row" onSubmit={start}>
        <label>
          Group
          <select value={form.group} required onChange={(e) => setForm({ ...form, group: e.target.value, log_group: "" })}>
            <option value="">—</option>
            {groups.data?.map((g) => <option key={g.id} value={g.name}>{g.name}</option>)}
          </select>
        </label>
        <label>
          Log group
          <select value={form.log_group} onChange={(e) => setForm({ ...form, log_group: e.target.value })}>
            <option value="">all in group</option>
            {lgOptions.map((l) => <option key={l.id} value={l.name}>{l.name}</option>)}
          </select>
        </label>
        <label className="grow">
          Pattern
          <input value={form.pattern} onChange={(e) => setForm({ ...form, pattern: e.target.value })} placeholder='{ $.level = "ERROR" }' />
        </label>
        <label>
          Types
          <select value={form.types} onChange={(e) => setForm({ ...form, types: e.target.value })}>
            <option value="log,error">logs + errors</option>
            <option value="log">logs</option>
            <option value="error,issue">errors + issue changes</option>
            <option value="log,error,issue">everything</option>
          </select>
        </label>
        <button type="submit">{status === "live" ? "Restart" : "Start"}</button>
        <button type="button" onClick={stop} disabled={status !== "live" && status !== "connecting"}>Stop</button>
      </form>
      <div className="row toolbar">
        <span role="status" className={`badge st-${status}`}>{status}</span>
        <button type="button" aria-pressed={paused} onClick={() => setPaused((p) => !p)}>
          {paused ? `Resume (${buffer.current.length} buffered)` : "Pause"}
        </button>
        <label className="inline">
          <input type="checkbox" checked={follow} onChange={(e) => setFollow(e.target.checked)} /> Follow
        </label>
        <button type="button" onClick={() => setLines([])}>Clear</button>
      </div>
      {error && <p role="alert" className="error">{error}</p>}
      <ul className="events tail" role="log" aria-live="off" aria-label="Live events">
        {lines.map((l) =>
          l.kind === "log" && l.event ? (
            <EventRow key={l.key} event={l.event} stream={l.stream} />
          ) : (
            <li key={l.key} className={`event special ${l.kind}`}>{l.text}</li>
          ),
        )}
      </ul>
      <div ref={bottom} />
    </section>
  );
}
